import { blogPosts } from "@/data/blogPosts";
import type { SeoLanguage } from "@/lib/seoMetadata";

type BlogSlug = (typeof blogPosts)[number]["slug"];
type LocalizedFirstQuestion = Record<SeoLanguage, string>;

const BLOG_FIRST_QUESTIONS_BY_SLUG: Record<BlogSlug, LocalizedFirstQuestion> = {
  "how-to-increase-claude-code-generation-quality-by-20-percent": {
    en: "How can teams improve AI code generation quality in real projects?",
    de: "Wie können Teams die Qualität KI-gestützter Code-Generierung in realen Projekten verbessern?",
    fr: "Comment les équipes peuvent-elles améliorer la qualité de génération de code IA dans des projets réels\u00a0?",
    it: "Come possono i team migliorare la qualità della generazione di codice con l'AI in progetti reali?",
    es: "¿Cómo pueden los equipos mejorar la calidad de generación de código con IA en proyectos reales?",
  },
  "shelpuk-ai-technology-consulting-recognized-as-a-top-ai-consulting-company": {
    en: "What does this recognition say about Shelpuk AI Technology Consulting's capabilities?",
    de: "Was sagt diese Auszeichnung über die Fähigkeiten von Shelpuk AI Technology Consulting aus?",
    fr: "Que dit cette distinction sur les compétences de Shelpuk AI Technology Consulting\u00a0?",
    it: "Cosa dice questo riconoscimento sulle capacità di Shelpuk AI Technology Consulting?",
    es: "¿Qué dice este reconocimiento sobre las capacidades de Shelpuk AI Technology Consulting?",
  },
  "double-your-llama-3-3-inference-speed-with-huggingface-text-generation-inference": {
    en: "How can teams speed up Llama 3.3 inference in production?",
    de: "Wie können Teams die Llama-3.3-Inferenz in der Produktion beschleunigen?",
    fr: "Comment les équipes peuvent-elles accélérer l'inférence de Llama 3.3 en production\u00a0?",
    it: "Come possono i team accelerare l'inferenza di Llama 3.3 in produzione?",
    es: "¿Cómo pueden los equipos acelerar la inferencia de Llama 3.3 en producción?",
  },
  "buy-vs-build-in-ai-making-the-optimal-technology-choice": {
    en: "How should teams decide whether to buy or build AI capabilities?",
    de: "Wie sollten Teams entscheiden, ob sie KI-Fähigkeiten kaufen oder selbst aufbauen?",
    fr: "Comment les équipes doivent-elles décider s'il faut acheter ou construire des capacités IA\u00a0?",
    it: "Come dovrebbero i team decidere se acquistare o costruire capacità AI?",
    es: "¿Cómo deberían los equipos decidir si conviene comprar o construir capacidades de IA?",
  },
  "meet-llama-3-2-edge-llm-vision-agentic-capabilities-and-more": {
    en: "What is new in Llama 3.2 for edge and multimodal use?",
    de: "Was ist neu an Llama 3.2 für Edge- und multimodale Anwendungsfälle?",
    fr: "Qu'apporte Llama 3.2 pour les usages edge et multimodaux\u00a0?",
    it: "Cosa c'è di nuovo in Llama 3.2 per uso edge e multimodale?",
    es: "¿Qué novedades trae Llama 3.2 para uso edge y multimodal?",
  },
  "so-you-want-to-explore-ai-for-your-company-where-to-start": {
    en: "Where should a company start when exploring AI opportunities?",
    de: "Wo sollte ein Unternehmen starten, wenn es KI-Chancen systematisch erkunden will?",
    fr: "Par où une entreprise devrait-elle commencer pour explorer des opportunités IA\u00a0?",
    it: "Da dove dovrebbe partire un'azienda per esplorare opportunità AI?",
    es: "¿Por dónde debería empezar una empresa para explorar oportunidades de IA?",
  },
  "fine-tuning-llama-3-1-with-swift-unsloth-alternative-for-multi-gpu-llm-training": {
    en: "What is SWIFT and when is it a better fine-tuning choice than Unsloth?",
    de: "Was ist SWIFT und wann ist es für Fine-Tuning die bessere Wahl als Unsloth?",
    fr: "Qu'est-ce que SWIFT et quand est-ce un meilleur choix de fine-tuning qu'Unsloth\u00a0?",
    it: "Che cos'è SWIFT e quando è una scelta di fine-tuning migliore di Unsloth?",
    es: "¿Qué es SWIFT y cuándo es una mejor opción de fine-tuning que Unsloth?",
  },
  "how-to-fine-tune-llama-3-1-with-unsloth-twice-as-fast-and-using-70-less-gpu-memory": {
    en: "What is Unsloth and how does it help with LLM fine-tuning?",
    de: "Was ist Unsloth und wie hilft es beim Fine-Tuning von LLMs?",
    fr: "Qu'est-ce qu'Unsloth et comment aide-t-il le fine-tuning de LLM\u00a0?",
    it: "Che cos'è Unsloth e come aiuta nel fine-tuning degli LLM?",
    es: "¿Qué es Unsloth y cómo ayuda en el fine-tuning de LLM?",
  },
  "low-rank-adaptation-lora-for-efficient-fine-tuning-of-large-language-models": {
    en: "What is LoRA and why is it used for efficient fine-tuning?",
    de: "Was ist LoRA und warum wird es für effizientes Fine-Tuning eingesetzt?",
    fr: "Qu'est-ce que LoRA et pourquoi est-ce utile pour un fine-tuning efficace\u00a0?",
    it: "Che cos'è LoRA e perché viene usato per un fine-tuning efficiente?",
    es: "¿Qué es LoRA y por qué se usa para un fine-tuning eficiente?",
  },
  "llm-practitioner-s-guide-gemma-a-game-changing-multilingual-llm": {
    en: "How good is Gemma for multilingual LLM use cases?",
    de: "Wie gut ist Gemma für mehrsprachige LLM-Anwendungsfälle?",
    fr: "Que vaut Gemma pour des cas d'usage LLM multilingues\u00a0?",
    it: "Quanto è adatto Gemma per casi d'uso LLM multilingue?",
    es: "¿Qué tan bueno es Gemma para casos de uso LLM multilingües?",
  },
  "llm-practitioner-s-guide-how-multilingual-falcon-mistral-smaug-and-other-llms-are": {
    en: "How multilingual are Falcon, Mistral, Smaug, and similar LLMs in practice?",
    de: "Wie mehrsprachig sind Falcon, Mistral, Smaug und ähnliche LLMs in der Praxis?",
    fr: "À quel point Falcon, Mistral, Smaug et des LLM similaires sont-ils multilingues en pratique\u00a0?",
    it: "Quanto sono multilingue, in pratica, Falcon, Mistral, Smaug e LLM simili?",
    es: "¿Qué tan multilingües son en la práctica Falcon, Mistral, Smaug y LLM similares?",
  },
  "llm-practitioner-s-guide-how-multilingual-llama-2-actually-is": {
    en: "How multilingual is Llama 2 in real-world evaluation?",
    de: "Wie mehrsprachig ist Llama 2 in realen Evaluierungen wirklich?",
    fr: "À quel point Llama 2 est-il multilingue dans des évaluations réelles\u00a0?",
    it: "Quanto è davvero multilingue Llama 2 in valutazioni reali?",
    es: "¿Qué tan multilingüe es Llama 2 en evaluaciones del mundo real?",
  },
  "shelpuk-ai-technology-consulting-honored-as-a-clutch-champion-and-clutch-global-for-2023": {
    en: "Why does this Clutch recognition matter for clients evaluating AI partners?",
    de: "Warum ist diese Clutch-Auszeichnung wichtig für Kunden, die KI-Partner evaluieren?",
    fr: "Pourquoi cette distinction Clutch compte-t-elle pour des clients qui évaluent des partenaires IA\u00a0?",
    it: "Perché questo riconoscimento Clutch conta per i clienti che valutano partner AI?",
    es: "¿Por qué este reconocimiento de Clutch importa para clientes que evalúan partners de IA?",
  },
  "the-tango-takes-two-building-synergy-between-ai-and-product-management": {
    en: "How should product management and AI engineering work together?",
    de: "Wie sollten Produktmanagement und KI-Engineering zusammenarbeiten?",
    fr: "Comment le product management et l'ingénierie IA devraient-ils travailler ensemble\u00a0?",
    it: "Come dovrebbero lavorare insieme product management e AI engineering?",
    es: "¿Cómo deberían trabajar juntos product management e ingeniería de IA?",
  },
  "competent-management-a-surprising-source-of-competitive-advantage": {
    en: "How does management quality become a competitive advantage in AI initiatives?",
    de: "Wie wird Managementqualität in KI-Initiativen zu einem Wettbewerbsvorteil?",
    fr: "Comment la qualité du management devient-elle un avantage concurrentiel dans les initiatives IA\u00a0?",
    it: "Come la qualità del management diventa un vantaggio competitivo nelle iniziative AI?",
    es: "¿Cómo la calidad de gestión se convierte en ventaja competitiva en iniciativas de IA?",
  },
  "can-chatgpt-build-a-competitive-advantage-for-your-company-and-if-so-how": {
    en: "Can ChatGPT create a real competitive advantage for a company?",
    de: "Kann ChatGPT für ein Unternehmen einen echten Wettbewerbsvorteil schaffen?",
    fr: "ChatGPT peut-il créer un véritable avantage concurrentiel pour une entreprise\u00a0?",
    it: "ChatGPT può creare un vero vantaggio competitivo per un'azienda?",
    es: "¿Puede ChatGPT crear una ventaja competitiva real para una empresa?",
  },
  "all-you-need-is-a-network-effect-how-great-tech-and-ai-products-compete": {
    en: "Why are network effects critical for AI product defensibility?",
    de: "Warum sind Netzwerkeffekte entscheidend für die Verteidigbarkeit von KI-Produkten?",
    fr: "Pourquoi les effets de réseau sont-ils cruciaux pour la défendabilité des produits IA\u00a0?",
    it: "Perché gli effetti di rete sono critici per la difendibilità dei prodotti AI?",
    es: "¿Por qué los efectos de red son críticos para la defensibilidad de productos de IA?",
  },
  "planning-for-a-new-ai-initiative-start-with-the-right-ai-development-culture": {
    en: "What AI development culture helps new initiatives succeed?",
    de: "Welche KI-Entwicklungskultur hilft neuen Initiativen, erfolgreich zu werden?",
    fr: "Quelle culture de développement IA aide les nouvelles initiatives à réussir\u00a0?",
    it: "Quale cultura di sviluppo AI aiuta le nuove iniziative ad avere successo?",
    es: "¿Qué cultura de desarrollo de IA ayuda a que nuevas iniciativas tengan éxito?",
  },
  "lacking-data-for-machine-learning-models-build-for-the-future-data": {
    en: "How can teams start an AI initiative when historical data is limited?",
    de: "Wie können Teams eine KI-Initiative starten, wenn historische Daten begrenzt sind?",
    fr: "Comment les équipes peuvent-elles lancer une initiative IA quand les données historiques sont limitées\u00a0?",
    it: "Come possono i team avviare un'iniziativa AI quando i dati storici sono limitati?",
    es: "¿Cómo pueden los equipos iniciar una iniciativa de IA cuando los datos históricos son limitados?",
  },
  "why-do-you-need-an-ai-strategy-ai-as-a-competitive-advantage": {
    en: "Why does a company need an explicit AI strategy to compete?",
    de: "Warum braucht ein Unternehmen eine explizite KI-Strategie, um wettbewerbsfähig zu sein?",
    fr: "Pourquoi une entreprise a-t-elle besoin d'une stratégie IA explicite pour rester compétitive\u00a0?",
    it: "Perché un'azienda ha bisogno di una strategia AI esplicita per competere?",
    es: "¿Por qué una empresa necesita una estrategia de IA explícita para competir?",
  },
  "what-is-your-ai-business-use-case-how-ai-can-help-and-harm-your-strategic-positioning": {
    en: "How do teams choose an AI use case that strengthens strategic positioning?",
    de: "Wie wählen Teams einen KI-Use-Case, der die strategische Positionierung stärkt?",
    fr: "Comment les équipes choisissent-elles un cas d'usage IA qui renforce le positionnement stratégique\u00a0?",
    it: "Come scelgono i team un caso d'uso AI che rafforzi il posizionamento strategico?",
    es: "¿Cómo eligen los equipos un caso de uso de IA que fortalezca el posicionamiento estratégico?",
  },
  "building-a-sustainable-ai-competitive-advantage-four-ai-designs-that-don-t-work": {
    en: "What AI designs do not create competitive advantage?",
    de: "Welche KI-Designs schaffen keinen Wettbewerbsvorteil?",
    fr: "Quels designs IA ne créent pas d'avantage concurrentiel\u00a0?",
    it: "Quali design AI non creano vantaggio competitivo?",
    es: "¿Qué diseños de IA no crean ventaja competitiva?",
  },
  "ai-strategies-that-offer-a-sustainable-competitive-advantage-principles-you-should-follow": {
    en: "Which AI strategy principles create durable competitive advantage?",
    de: "Welche KI-Strategieprinzipien schaffen einen dauerhaften Wettbewerbsvorteil?",
    fr: "Quels principes de stratégie IA créent un avantage concurrentiel durable\u00a0?",
    it: "Quali principi di strategia AI creano un vantaggio competitivo duraturo?",
    es: "¿Qué principios de estrategia de IA crean ventaja competitiva duradera?",
  },
};

export function getBlogFirstQuestionBySlug(slug: string, lang: SeoLanguage) {
  if (!(slug in BLOG_FIRST_QUESTIONS_BY_SLUG)) {
    return null;
  }

  return BLOG_FIRST_QUESTIONS_BY_SLUG[slug as BlogSlug][lang];
}
